import React from 'react'
import { Form } from 'antd'
import { QuestionnaireItem } from '@medplum/fhirtypes'
import { useQuery } from '@tanstack/react-query'
import { debounce } from 'lodash'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import { QuestionLayout } from 'components/Assessments/components/types'
import { TextAreaQuestion } from 'components/Assessments/components/QuestionComponents'
import { useQAAssessmentActionsContext } from 'contexts/QAAssessmentActionsContext'
import { useQAAssessmentCommentsContext } from 'contexts/QAAssessmentCommentsContext'
import { getCommentQuestionLinkId } from 'components/QA/Assessments/helpers'
import { DEFAULT_REQUIRED_FIELD_MESSAGE } from 'components/Assessments/constants'
import { dataFormatMappers } from 'components/Assessments/constants'
import { DataFetchProvider, useDataFetch } from 'contexts/DataFetchContext'
import useDynamicDataFetcher from 'components/Assessments/hooks/useDynamicDataFetcher'
import {
  QUESTIONNAIRE_API_ENDPOINT_EXTENSION_URL,
  QUESTIONNAIRE_API_SEARCH_PARAMS_EXTENSION_URL
} from 'fhir/Questionnaire/constants'
import { getCodeableConceptExtensionValue } from 'fhir/utils'
import { QueryKeys } from 'utils/query.constants'
import { getEquipments } from 'api/static'
import { PatientBackgroundFormField } from 'components/Assessments/PatAssessment/sections/PatientBackground/formField'
import { PatientBackgroundQuestionnaireItem } from 'components/Assessments/PatAssessment/sections/PatientBackground/questionnaire'
import {
  CodingMultiSelectQuestion,
  CodingRadioQuestionWithNotes,
  CodingSelectQuestion
} from 'components/Assessments/PatAssessment/components'
import type { CodingSelectOption } from 'components/Assessments/PatAssessment/components'
import { isRawDiagnosisArray } from 'components/Assessments/PatAssessment/components/utils'
import { normalizeEquipmentCode } from 'components/Assessments/PatAssessment/helpers'
import SectionContainer from 'components/Assessments/components/SectionContainer'
import { OTHER_EQUIPMENT_OPTION, OTHER_EQUIPMENT_OPTION_CODE } from './constants'

interface HistoryAndDiagnosesSubsectionProps {
  subsection: QuestionnaireItem
  fields: Record<string, QuestionnaireItem>
  cantEditAssessmentFields: boolean
  assessmentMustBeFinalized: boolean
  therapyOptions: CodingSelectOption[]
}

function HistoryAndDiagnosesSubsectionContent({
  subsection,
  fields,
  cantEditAssessmentFields,
  assessmentMustBeFinalized,
  therapyOptions
}: HistoryAndDiagnosesSubsectionProps): JSX.Element {
  const form = Form.useFormInstance()
  const {
    assessmentStatusState: { inQA, inQAEdit }
  } = useAssessmentContext()
  const { createCommentClickHandler, disableHover } = useQAAssessmentActionsContext()
  const { sortedComments: comments, focusedCommentId } = useQAAssessmentCommentsContext()
  const focusedComment = comments.find((c) => c.id === focusedCommentId)
  const focusedCommentLinkId = focusedComment ? getCommentQuestionLinkId(focusedComment) : null

  // Fetch dynamic data for diagnoses
  const { data: dynamicData, loading: dynamicDataLoading } = useDynamicDataFetcher({
    sectionData: subsection
  })
  const { data: equipment, isLoading: isLoadingEquipment } = useQuery({
    queryKey: [QueryKeys.EQUIPMENT],
    queryFn: getEquipments,
    select: (data) => {
      const equipmentOptions = data
        .map(({ name }) => {
          if (!name) return null

          const normalizedCode = normalizeEquipmentCode(name)
          return {
            value: normalizedCode,
            label: name,
            code: normalizedCode,
            display: name
          }
        })
        .filter((item): item is CodingSelectOption => item !== null)

      if (!equipmentOptions.some((item) => item.value === OTHER_EQUIPMENT_OPTION_CODE)) {
        equipmentOptions.push({
          value: OTHER_EQUIPMENT_OPTION_CODE,
          label: OTHER_EQUIPMENT_OPTION,
          code: OTHER_EQUIPMENT_OPTION_CODE,
          display: OTHER_EQUIPMENT_OPTION
        })
      }

      return equipmentOptions
    }
  })

  // Extract the diagnoses endpoint from the subsection extension
  const diagnosesEndpoint = React.useMemo(() => {
    return (
      subsection?.extension?.find((ext) => ext.url === QUESTIONNAIRE_API_ENDPOINT_EXTENSION_URL)
        ?.valueCodeableConcept?.coding?.[0]?.code || 'static/diagnoses'
    )
  }, [subsection])

  // Format diagnoses data - use code as value, display as label
  const diagnosesData = React.useMemo(() => {
    const rawDiagnosesValue = dynamicData?.[diagnosesEndpoint]

    if (!isRawDiagnosisArray(rawDiagnosesValue)) {
      return []
    }

    const rawDiagnoses = rawDiagnosesValue

    return rawDiagnoses.map((item) => {
      const mapped = dataFormatMappers.diagnoses(item)
      return {
        label: mapped.label,
        value: mapped.code,
        code: mapped.code,
        display: mapped.value
      }
    })
  }, [dynamicData, diagnosesEndpoint])

  const primaryDiagnosesField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_PRIMARY_DIAGNOSES]
  const otherDiagnosesField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_OTHER_DIAGNOSES]
  const therapiesField = fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_THERAPIES]
  const equipmentField = fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_EQUIPMENT]
  const equipmentOtherField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_EQUIPMENT_OTHER]
  const erQuestionField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_ER_QUESTION]
  const erNotesField = fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_ER_NOTES]
  const hospitalQuestionField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_HOSPITAL_QUESTION]
  const hospitalNotesField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_HOSPITAL_NOTES]
  const injuriesQuestionField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_INJURIES_QUESTION]
  const injuriesNotesField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_INJURIES_NOTES]
  const fallsQuestionField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_FALLS_QUESTION]
  const fallsNotesField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_FALLS_NOTES]
  const infectionsQuestionField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_INFECTIONS_QUESTION]
  const infectionsNotesField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_INFECTIONS_NOTES]
  const mdQuestionField =
    fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_MD_QUESTION]
  const mdNotesField = fields[PatientBackgroundQuestionnaireItem.HISTORY_AND_DIAGNOSES_MD_NOTES]

  const { setSearchQuery, setOffset } = useDataFetch()

  const searchParam = React.useMemo(() => {
    if (!primaryDiagnosesField) return null
    const searchParamExtension = getCodeableConceptExtensionValue(
      primaryDiagnosesField.extension || [],
      QUESTIONNAIRE_API_SEARCH_PARAMS_EXTENSION_URL
    )
    return searchParamExtension?.coding?.[0]?.code || null
  }, [primaryDiagnosesField])

  const handleDiagnosesSearch = React.useCallback(
    (searchValue: string): void => {
      if (searchParam) {
        setSearchQuery({ param: searchParam, value: searchValue })
        setOffset(0)
      }
    },
    [searchParam, setSearchQuery, setOffset]
  )

  const debouncedHandleDiagnosesSearch = React.useMemo(
    () => debounce(handleDiagnosesSearch, 300),
    [handleDiagnosesSearch]
  )

  // Cancel pending debounced callbacks on unmount or when handleDiagnosesSearch changes
  React.useEffect(() => {
    return () => {
      debouncedHandleDiagnosesSearch.cancel()
    }
  }, [debouncedHandleDiagnosesSearch])

  const handleDiagnosesScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>): void => {
      if (searchParam) {
        const threshold = 5
        const target = e.target as HTMLDivElement
        const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + threshold
        if (bottom) {
          setOffset((prevOffset: number) => (prevOffset || 0) + 50)
        }
      }
    },
    [searchParam, setOffset]
  )

  // Reset search query and offset when dropdown closes to prevent stale filter state
  const handleDiagnosesDropdownVisibleChange = React.useCallback(
    (open: boolean): void => {
      if (!open && searchParam) {
        setSearchQuery({ param: searchParam, value: '' })
        setOffset(0)
      }
    },
    [searchParam, setSearchQuery, setOffset]
  )

  const equipmentValue = Form.useWatch(PatientBackgroundFormField.EQUIPMENT, form)
  const isOtherEquipmentOptionSelected = React.useMemo(
    () =>
      Array.isArray(equipmentValue) &&
      equipmentValue.some((item) => item?.code?.toLowerCase() === OTHER_EQUIPMENT_OPTION_CODE),
    [equipmentValue]
  )
  const handleEquipmentChange = React.useCallback(
    (selectedCodes: string[]) => {
      const hasOtherSelected = selectedCodes.some(
        (selectedCode) => selectedCode.toLowerCase() === OTHER_EQUIPMENT_OPTION_CODE
      )
      if (!hasOtherSelected) {
        form.setFieldValue(PatientBackgroundFormField.EQUIPMENT_OTHER, undefined)
      }
    },
    [form]
  )

  return (
    <SectionContainer subsection={subsection}>
      {primaryDiagnosesField && (
        <CodingSelectQuestion
          field={primaryDiagnosesField}
          formFieldName={PatientBackgroundFormField.PRIMARY_DIAGNOSES}
          layout={QuestionLayout.HORIZONTAL}
          disabled={cantEditAssessmentFields}
          customRules={[
            {
              required: Boolean(assessmentMustBeFinalized && primaryDiagnosesField.required),
              message: 'Please add a valid response'
            }
          ]}
          options={diagnosesData || []}
          loading={dynamicDataLoading}
          selectProps={{
            onSearch: debouncedHandleDiagnosesSearch,
            onPopupScroll: handleDiagnosesScroll,
            onDropdownVisibleChange: handleDiagnosesDropdownVisibleChange,
            filterOption: false,
            optionFilterProp: 'label'
          }}
        />
      )}

      {otherDiagnosesField && (
        <CodingMultiSelectQuestion
          field={otherDiagnosesField}
          formFieldName={PatientBackgroundFormField.OTHER_DIAGNOSES}
          layout={QuestionLayout.HORIZONTAL}
          disabled={cantEditAssessmentFields}
          customRules={[
            {
              required: Boolean(assessmentMustBeFinalized && otherDiagnosesField.required),
              message: 'Please add a valid response'
            }
          ]}
          options={diagnosesData || []}
          loading={dynamicDataLoading}
          selectProps={{
            onSearch: debouncedHandleDiagnosesSearch,
            onPopupScroll: handleDiagnosesScroll,
            onDropdownVisibleChange: handleDiagnosesDropdownVisibleChange,
            filterOption: false,
            optionFilterProp: 'label'
          }}
        />
      )}

      {therapiesField && (
        <CodingMultiSelectQuestion
          field={therapiesField}
          formFieldName={PatientBackgroundFormField.THERAPIES}
          layout={QuestionLayout.HORIZONTAL}
          disabled={cantEditAssessmentFields}
          customRules={[
            {
              required: Boolean(assessmentMustBeFinalized && therapiesField.required),
              message: 'Please add a valid response'
            }
          ]}
          options={therapyOptions}
          selectProps={{
            optionFilterProp: 'label',
            filterOption: true
          }}
        />
      )}

      {equipmentField && (
        <CodingMultiSelectQuestion
          field={equipmentField}
          formFieldName={PatientBackgroundFormField.EQUIPMENT}
          layout={QuestionLayout.HORIZONTAL}
          disabled={cantEditAssessmentFields}
          customRules={[
            {
              required: Boolean(assessmentMustBeFinalized && equipmentField.required),
              message: 'Please add a valid response'
            }
          ]}
          options={equipment || []}
          loading={isLoadingEquipment}
          selectProps={{
            onChange: handleEquipmentChange,
            optionFilterProp: 'label',
            filterOption: true
          }}
        />
      )}

      {equipmentOtherField && isOtherEquipmentOptionSelected && !isLoadingEquipment && (
        <TextAreaQuestion
          field={equipmentOtherField}
          formFieldName={PatientBackgroundFormField.EQUIPMENT_OTHER}
          layout={QuestionLayout.HORIZONTAL}
          disabled={cantEditAssessmentFields}
          required={assessmentMustBeFinalized || isOtherEquipmentOptionSelected}
        />
      )}

      {erQuestionField && (
        <CodingRadioQuestionWithNotes
          field={erQuestionField}
          formFieldName={PatientBackgroundFormField.ER_QUESTION}
          notesField={erNotesField}
          notesFormFieldName={PatientBackgroundFormField.ER_NOTES}
          disabled={cantEditAssessmentFields}
          customRules={[
            {
              required: Boolean(assessmentMustBeFinalized && erQuestionField.required),
              message: DEFAULT_REQUIRED_FIELD_MESSAGE
            }
          ]}
          inQA={inQA}
          inQAEdit={inQAEdit}
          disableHover={disableHover}
          isFocused={erQuestionField.linkId === focusedCommentLinkId}
          onCommentClick={() =>
            erQuestionField.linkId && createCommentClickHandler(erQuestionField.linkId)
          }
        />
      )}

      {hospitalQuestionField && (
        <CodingRadioQuestionWithNotes
          field={hospitalQuestionField}
          formFieldName={PatientBackgroundFormField.HOSPITAL_QUESTION}
          notesField={hospitalNotesField}
          notesFormFieldName={PatientBackgroundFormField.HOSPITAL_NOTES}
          disabled={cantEditAssessmentFields}
          customRules={[
            {
              required: Boolean(assessmentMustBeFinalized && hospitalQuestionField.required),
              message: DEFAULT_REQUIRED_FIELD_MESSAGE
            }
          ]}
          inQA={inQA}
          inQAEdit={inQAEdit}
          disableHover={disableHover}
          isFocused={hospitalQuestionField.linkId === focusedCommentLinkId}
          onCommentClick={() =>
            hospitalQuestionField.linkId && createCommentClickHandler(hospitalQuestionField.linkId)
          }
        />
      )}

      {injuriesQuestionField && (
        <CodingRadioQuestionWithNotes
          field={injuriesQuestionField}
          formFieldName={PatientBackgroundFormField.INJURIES_QUESTION}
          notesField={injuriesNotesField}
          notesFormFieldName={PatientBackgroundFormField.INJURIES_NOTES}
          disabled={cantEditAssessmentFields}
          customRules={[
            {
              required: Boolean(assessmentMustBeFinalized && injuriesQuestionField.required),
              message: DEFAULT_REQUIRED_FIELD_MESSAGE
            }
          ]}
          inQA={inQA}
          inQAEdit={inQAEdit}
          disableHover={disableHover}
          isFocused={injuriesQuestionField.linkId === focusedCommentLinkId}
          onCommentClick={() =>
            injuriesQuestionField.linkId && createCommentClickHandler(injuriesQuestionField.linkId)
          }
        />
      )}

      {fallsQuestionField && (
        <CodingRadioQuestionWithNotes
          field={fallsQuestionField}
          formFieldName={PatientBackgroundFormField.FALLS_QUESTION}
          notesField={fallsNotesField}
          notesFormFieldName={PatientBackgroundFormField.FALLS_NOTES}
          disabled={cantEditAssessmentFields}
          customRules={[
            {
              required: Boolean(assessmentMustBeFinalized && fallsQuestionField.required),
              message: DEFAULT_REQUIRED_FIELD_MESSAGE
            }
          ]}
          inQA={inQA}
          inQAEdit={inQAEdit}
          disableHover={disableHover}
          isFocused={fallsQuestionField.linkId === focusedCommentLinkId}
          onCommentClick={() =>
            fallsQuestionField.linkId && createCommentClickHandler(fallsQuestionField.linkId)
          }
        />
      )}

      {infectionsQuestionField && (
        <CodingRadioQuestionWithNotes
          field={infectionsQuestionField}
          formFieldName={PatientBackgroundFormField.INFECTIONS_QUESTION}
          notesField={infectionsNotesField}
          notesFormFieldName={PatientBackgroundFormField.INFECTIONS_NOTES}
          disabled={cantEditAssessmentFields}
          customRules={[
            {
              required: Boolean(assessmentMustBeFinalized && infectionsQuestionField.required),
              message: DEFAULT_REQUIRED_FIELD_MESSAGE
            }
          ]}
          inQA={inQA}
          inQAEdit={inQAEdit}
          disableHover={disableHover}
          isFocused={infectionsQuestionField.linkId === focusedCommentLinkId}
          onCommentClick={() =>
            infectionsQuestionField.linkId &&
            createCommentClickHandler(infectionsQuestionField.linkId)
          }
        />
      )}

      {mdQuestionField && (
        <CodingRadioQuestionWithNotes
          field={mdQuestionField}
          formFieldName={PatientBackgroundFormField.MD_QUESTION}
          notesField={mdNotesField}
          notesFormFieldName={PatientBackgroundFormField.MD_NOTES}
          disabled={cantEditAssessmentFields}
          customRules={[
            {
              required: Boolean(assessmentMustBeFinalized && mdQuestionField.required),
              message: DEFAULT_REQUIRED_FIELD_MESSAGE
            }
          ]}
          inQA={inQA}
          inQAEdit={inQAEdit}
          disableHover={disableHover}
          isFocused={mdQuestionField.linkId === focusedCommentLinkId}
          onCommentClick={() =>
            mdQuestionField.linkId && createCommentClickHandler(mdQuestionField.linkId)
          }
        />
      )}
    </SectionContainer>
  )
}

function HistoryAndDiagnosesSubsection(props: HistoryAndDiagnosesSubsectionProps): JSX.Element {
  return (
    <DataFetchProvider>
      <HistoryAndDiagnosesSubsectionContent {...props} />
    </DataFetchProvider>
  )
}

export default HistoryAndDiagnosesSubsection
